/**
 * subject.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Observable } from "rxjs/Rx";

import { Algorithm } from "../algorithms/structure/algorithm";
import { Group } from "../auth/identity/group";
import { TypedDataHolder } from "../common/data/typed-data";

import { Comment } from "../intake-form/comment";
import { MessageSender } from "../server/message-sender";
import {
    CreateSubjectContextRequest,
    ReleaseObjectRequest,
    SubjectContextResponse
} from "../server/messages/context-management/";
import {
    AddSubjectContextRelationshipRequest,
    DeleteSubjectContextRelationshipRequest,
    ModifySubjectContextRelationshipRequest
} from "../server/messages/subjects/";
import { descriptor } from "../support/types/decorators";
import { arrayRemove } from "../support/utilities/functions";

import { Relationship, RelationshipType, Role } from "./relationship";
import { KnownSubjectContextTypes, SubjectContextType }
    from "./subject-context-type";
import { SubjectReference } from "./subject-reference";

/**
 * A callback to identify an Algorithm by its formal name.
 */
type AlgorithmResolver = (name : string) => Algorithm;

/**
 * The Subject is the primary kind of database record. It represents one unit
 * that can be fed into the rules engine for a particular algorithm.
 *
 * In ABC's healthcare application, it represents a single person.
 */
export class Subject
{
    /**
     * The type of the subject, which identifies its purpose, what data it
     * contains, and which algorithms it can run.
     */
    public type : SubjectContextType;

    /**
     * The algorithms that this subject can run on.
     */
    public availableAlgorithms : Algorithm[] | null;

    /**
     * The identifiers that refer to this subject and can be used to load it.
     */
    public identifiers : SubjectReference[] = [];

    /**
     * Whether the subject is currently held in the server's cache.
     */
    public loaded : boolean = false;

    /**
     * Whether the subject is engaged in a running algorithm.
     */
    public running : boolean = false;

    /**
     * The opaque ID that represents the subject while loaded by the server.
     */
    public id : number | null = null;

    /**
     * Used to debug other references to the same server ID.
     */
    public cloneOf : Subject;

    /**
     * An static incrementing ID allocator for getting unique local IDs
     */
    public static nextLocalId : number = 0;

    /**
     * This subject's unique local ID.
     */
    public localId : number = Subject.nextLocalId++;

    /**
     * The ID for subscribing and unsubscribing.
     */
    public incomingWatchId : number | null;

    /**
     * The identifier that was last used to load the subject into memory.
     */
    public lastIdentifier : SubjectReference;

    /**
     * The time this object was created on the client.
     */
    public localCreated : Date = new Date();

    /**
     * The time this context was created, as identified by the server.
     */
    public created : Date;

    /**
     * The timestamp of the most recent client action performed.
     */
    public lastLocalActionTime : Date = new Date();

    /**
     * Short descriptor of the most recent client action performed.
     */
    public lastLocalActionDescription : string = "loaded";

    /**
     * Whether this subject is new or has been saved before.
     */
    public newlyCreated : boolean = false;

    /**
     * Which algorithm the subject was created initially targetting.
     */
    public initialAlgorithmTarget : Algorithm;

    /**
     * The groups the subject is visible to.
     */
    public groups : Group[] = [];

    /**
     * {@link Comment}s that are associated directly with the {@link Subject}.
     */
    public comments : Comment[] = [];

    // TODO!
    public summary : TypedDataHolder[];

    /**
     * The relationships this subject partakes in.
     */
    public relationships : Relationship[] = [];

    /**
     * Whether the subject has groups that the user *could* see, but aren't
     * visible in this zone.
     */
    public hasHiddenGroups : boolean;

    public lastRefType : string;
    public lastRefURL? : string;

    constructor (
        protected sender : MessageSender,
        private algorithmResolver : AlgorithmResolver,
        ref? : SubjectReference)
    {
        if (ref)
        {
            this.identifiers = [ref];
        }
    }

    public assertTypeDefined () : void
    {
        if (!this.type)
        {
            console.error(
                `Subject had no type when type was required!`,
                this);
            throw new Error(`Subject had no type when type was required!`);
        }
    }

    /**
     * Attempt to load the subject into the server's primary cache so it can be
     * accessed and manipulated.
     */
    public load () : Observable<SubjectContextResponse>
    {
        if (this.loaded)
        {
            console.warn("Requested load of already loaded subject", this);
            return Observable.empty();
        }
        const identifier : SubjectReference = this.findPreferredReference();
        if (identifier)
        {
            return this.loadVia(identifier);
        }
        else
        {
            console.error("Could not find appropriate ID for subject", this);
            throw new Error("Subject not loadable - no valid ID");
        }
    }

    /**
     * Turn a particular reference into a protocol request to load the subject.
     */
    public loadVia (identifier : SubjectReference)
        : Observable<SubjectContextResponse>
    {
        this.lastRefType = identifier.type;
        if (identifier.summaryURL) {
            this.lastRefURL = identifier.summaryURL;
        } else {
            this.lastRefURL = null;
        }
        return this.sender.converse(
            identifier.request
        ) as Observable<SubjectContextResponse>; // TODO: check?
    }

    /**
     * Signal to the server that the corresponding resource for this subject may
     * be released from memory.
     */
    public release () : Observable<boolean>
    {
        if (!this.loaded)
        {
            console.error("Requested release of non-loaded subject", this);
            throw new Error("Cannot release non-loaded subject!");
        }
        return this.sender.successful(new ReleaseObjectRequest(this.id)).do(
            () => {
                this.loaded = false;
                this.id = null;
            });
    }

    /**
     * Find the "best" way to load a subject.
     */
    private findPreferredReference () : SubjectReference
    {
        return this.identifiers.slice(-1)[0]; // we just return the last entry
    }

    /**
     * Find the first occurence of a particular reference type.
     */
    public findRef (kind : string) : SubjectReference | null
    {
        return this.identifiers.find(
            (sr : SubjectReference) => sr.type === kind);
    }

    /**
     * Remove a subject reference.
     */
    public removeRef (ref : SubjectReference) : void
    {
        arrayRemove(this.identifiers, ref);
    }

    /**
     * Attach a reference to this subject, overwriting an existing reference of
     * the same type, if applicable.
     */
    public addRef (ref : SubjectReference, overwrite : boolean = true) : void
    {
        const existing : number = this.identifiers.findIndex(
            (sr : SubjectReference) =>
                (sr.type === ref.type && sr.host === ref.host));
        if ((existing !== -1) && overwrite)
        {
            this.identifiers[existing] = ref;
        }
        else
        {
            this.identifiers.push(ref);
        }
        this.lastIdentifier = ref;
    }

    /**
     * Read the incoming data from a successful load.
     */
    public readSubjectResult (scr : SubjectContextResponse) : true
    {
        this.loaded = true;
        this.id = scr.objectId;
        this.type = scr.contextType;
        this.incomingWatchId = scr.watchId;
        this.created = new Date(scr.created);
        this.availableAlgorithms = scr.algorithms.map(
            (name : string) => this.algorithmResolver(name));
        this.summary = scr.summary.summary;
        return true;
    }

    /**
     * Delete a {@link Relationship}.
     * @param rel The relationship to remove
     */
    public removeRelationship (rel : Relationship) : Observable<boolean>
    {
        if (this.relationships.indexOf(rel) === -1)
        {
            throw new Error("Subject does not contain rel slated for delete!");
        }
        return this.sender.resultOf(
            new DeleteSubjectContextRelationshipRequest(this.id, rel.id));
    }

    /**
     * Attempt to construct a relationship between this and another Subject.
     * @param partner The target subject
     * @param type The {@link RelationshipType}
     * @param sourceRole The role for this subject
     * @param targetRole The role for the partner
     */
    public relateTo (
        partner : Subject,
        type : RelationshipType,
        sourceRole : Role,
        targetRole : Role
    ) : Observable<boolean>
    {
        if (!partner.loaded || !this.loaded)
        {
            console.error(this, partner);
            throw new Error("Both subjects must be loaded to relate the two");
        }
        return this.sender.resultOf(new AddSubjectContextRelationshipRequest(
            type.name,
            this.id,
            sourceRole.name,
            partner.id,
            targetRole.name
        ), true);
    }

    public updateRelation (
        relationshipId : number,
        sourceRole : Role,
        targetRole : Role
    ) : Observable<boolean>
    {
        return this.sender.resultOf(new ModifySubjectContextRelationshipRequest(
            this.id,
            relationshipId,
            sourceRole.name,
            targetRole.name
        ), true);
    }

    /**
     * Retrieve a particular named "column" of the subject's summary data.
     */
    public getSummaryColumn (name : string) : {value : any}
    {
        if (!this.summary)
        {
            return null;
        }
        return this.summary.find((o : any) => o && o.name === name);
    }

    public static getSummaryOn (name : string, summary : TypedDataHolder[])
      : {value : any}
    {
      if (!Array.isArray(summary))
      {
        return null;
      }
      return summary.find((o : any) => o && o.name === name);
    }

    /**
     * Cheat your way to a human readable short label for the subject.
     * You should generally use the full summary to identify the subject to
     * users (because you can't apprehend how they will chose to find and
     * disambiguate the data). This method hard-codes specific state variable
     * lookups, which is a terrible idea, but helpful for labelling during
     * the presentation of debug and error data.
     */
    public get readableTitle () : string
    {
        return Subject.readableTitleFor(this.summary, this.type);
    }

    public static readableTitleFor (
      summary : TypedDataHolder[],
      sct : SubjectContextType
    ) : string
    {
      if (Array.isArray(summary))
      {
          if (sct === KnownSubjectContextTypes.Healthcare)
          {
              return (Subject.getSummaryOn("first name", summary).value
                  + " "
                  + Subject.getSummaryOn("last name", summary).value);
          }
          else if (sct === KnownSubjectContextTypes.IssueReport)
          {
              return Subject.getSummaryOn("IR issue title", summary).value;
          }
          return `Unknown ${sct}`;
      }
      return `Empty summary`;
    }

    public get abbreviatedTitle () : string
    {
        return Subject.abbreviatedTitleFor(this.summary, this.type);
    }

    public static abbreviatedTitleFor (
      summary : TypedDataHolder[],
      sct : SubjectContextType
    ) : string
    {
      if (Array.isArray(summary))
      {
          if (sct === KnownSubjectContextTypes.Healthcare)
          {
              let result : string = (
                  (Subject.getSummaryOn("last name", summary).value as string
                    || "").substring(0, 3).toLocaleUpperCase()
                  + "," +
                  (Subject.getSummaryOn("first name", summary).value as string 
                    || "").substring(0, 3).toLocaleUpperCase()
                );
              if (result === ",")
              {
                  return "«new entry»";
              }
              else
              {
                  return result;
              }
          }
          else if (sct === KnownSubjectContextTypes.IssueReport)
          {
              return Subject.getSummaryOn("IR issue title", summary).value;
          }
          return `Unknown ${sct}`;
      }
      return `Empty summary`;
    }

    public sameAs (compare : Subject) : boolean
    {
        return (
            (this === compare)
            || this.id === compare.id // shouldn't happen anymore
            || this.cloneOf === compare
            || compare.cloneOf === this
        );
    }

    public touch (description : string) : void
    {
      this.lastLocalActionTime = new Date();
      this.lastLocalActionDescription = description || "unknown";
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/subjects/subject.ts